import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { apiClient } from "../../apiclient/apiclient";
import { toast } from "react-toastify";
import Loader from "../../components/Loader";
import CreatableSelect from "react-select/creatable";
const inputClass =
  "border border-slate-200 focus:border-blue-400 focus:ring-2 focus:ring-blue-100 rounded-lg px-3 py-2 text-sm bg-white outline-none transition w-full";

const labelClass = "text-xs font-medium text-slate-700";

const AddClient = ({ isOpen, onClose, onAddClient, clientData, isEdit }) => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    companyName: "",
    address: "",
    city: "",
    state: "",
    zipCode: "",
    country: "",
    loyaltyPoints: 0,
    openingBalance: 0,
    openingBalanceType: "debit", // ✅ FIX 1
    businessType: "",
  });

  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [cityOptions, setCityOptions] = useState([]);

  useEffect(() => {
    const fetchCities = async () => {
      try {
        const res = await apiClient.get("/clients/cities/unique");
        if (res.data.success && res.data.cities) {
          setCityOptions(res.data.cities.map(city => ({ label: city, value: city })));
        }
      } catch (err) {
        console.error("Failed to fetch cities", err);
      }
    };
    if (isOpen) {
      fetchCities();
    }
  }, [isOpen]);

  /* ================= PREFILL ================= */
  useEffect(() => {
    if (isEdit && clientData) {
      

      setFormData({
        name: clientData.name || "",
        email: clientData.email || "",
        phone: clientData.phone || "",
        companyName: clientData.companyName || "",
        address: clientData.address || "",
        city: clientData.city || "",
        state: clientData.state || "",
        zipCode: clientData.zipCode || "",
        country: clientData.country || "",
        loyaltyPoints: Number(clientData.loyaltyPoints || 0),
        openingBalance: Number(clientData.openingBalance || 0),
        openingBalanceType: clientData.openingBalanceType || "debit",
        businessType: clientData.businessType || "",
      });

    } else {
      resetForm();
    }
  }, [isOpen, clientData, isEdit]);

  /* ================= HANDLERS ================= */
  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: ["openingBalance", "loyaltyPoints"].includes(name) ? Number(value || 0) : value,
    }));

    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: "" }));
  };

 const validateForm = () => {
  const newErrors = {};

  if (!formData.name.trim()) {
    newErrors.name = "Client name is required";
  }

  if (!formData.phone.trim()) {
    newErrors.phone = "Contact number is required";
  } else if (!/^[0-9]{10}$/.test(formData.phone)) {
    newErrors.phone = "Enter a valid 10-digit contact number";
  }

  // Email optional but validated if entered
  if (formData.email.trim()) {
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "Invalid email format";
    }
  }

  if (formData.openingBalance < 0) {
    newErrors.openingBalance = "Opening balance cannot be negative";
  }


  return newErrors;
};


  const handleSubmit = async (e) => {
    e.preventDefault();

    const newErrors = validateForm();
    if (Object.keys(newErrors).length) {
      setErrors(newErrors);
      return;
    }

    setLoading(true);
    try {
      let res;

      if (isEdit) {
        res = await apiClient.put(`/clients/${clientData._id}`, formData);
        toast.success("Client updated successfully");
        onAddClient(res.data?.client, true);
      } else {
        res = await apiClient.post("/clients", formData);
       toast.success(res.data.message); // "Client created successfully"
// ✅ Toast ALWAYS works
        onAddClient(res.data?.client, false);
      }

      resetForm();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to save client");
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: "",
      email: "",
      phone: "",
      companyName: "",
      address: "",
      city: "",
      state: "",
      zipCode: "",
      country: "",
      loyaltyPoints: 0,
      openingBalance: 0,
      openingBalanceType: "debit", // ✅ FIX 2
      businessType: "",
    });
    setErrors({});
  };

  if (!isOpen) return null;

  /* ================= UI ================= */
  return createPortal(
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[100] p-2">
      <form
        onSubmit={handleSubmit}
        className="bg-white rounded-xl shadow-lg w-full max-w-3xl max-h-[90vh] flex flex-col overflow-hidden"
      >
        {/* HEADER */}
        <div className="px-6 py-4 bg-[#172554] border-b flex justify-between items-center text-white">
          <h2 className="text-lg font-semibold">
            {isEdit ? "Edit Client" : "Add New Client"}
          </h2>
          <button
            type="button"
            onClick={() => {
              resetForm();
              onClose();
            }}
            className="text-xl text-white"
          >
            ✕
          </button>
        </div>

        {/* FORM BODY - Scrollable */}
        <div className="flex-1 overflow-y-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-2 p-6">
            {/* Name */}
            <div className="flex flex-col gap-1">
              <label className={labelClass}>Name *</label>
              <input name="name" value={formData.name} onChange={handleChange} className={inputClass} />
              {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name}</p>}
            </div>

            {/* Email */}
            <div className="flex flex-col gap-1">
              <label className={labelClass}>Email</label>
              <input name="email" value={formData.email} onChange={handleChange} className={inputClass} />
              {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
            </div>

            {/* Phone */}
            <div className="flex flex-col gap-1">
              <label className={labelClass}>Phone *</label>
              <input name="phone" value={formData.phone} onChange={handleChange} className={inputClass} />
              {errors.phone && <p className="text-red-500 text-xs mt-1">{errors.phone}</p>}
            </div>

            {/* Company Name */}
            <div className="flex flex-col gap-1">
              <label className={labelClass}>Company Name</label>
              <input name="companyName" value={formData.companyName} onChange={handleChange} className={inputClass} />
            </div>

            {/* Client Type */}
            <div className="flex flex-col gap-1">
              <label className={labelClass}>Client Type</label>
              <select
                name="businessType"
                value={formData.businessType}
                onChange={handleChange}
                className={inputClass}
              >
                <option value="">Select Client Type</option>
                <option value="wholesale">Wholesale Shop Owner</option>
                <option value="gym">Gym Owner</option>
              </select>
            </div>

            {/* Address */}
            <div className="flex flex-col gap-1">
              <label className={labelClass}>Address</label>
              <input name="address" value={formData.address} onChange={handleChange} className={inputClass} />
            </div>

            {/* City */}
            <div className="flex flex-col gap-1">
              <label className={labelClass}>City</label>
              <CreatableSelect
                isClearable
                options={cityOptions}
                value={formData.city ? { label: formData.city, value: formData.city } : null}
                onChange={(selectedOption) => {
                  setFormData((prev) => ({
                    ...prev,
                    city: selectedOption ? selectedOption.value : "",
                  }));
                }}
                onCreateOption={(inputValue) => {
                  setFormData((prev) => ({ ...prev, city: inputValue }));
                  setCityOptions((prev) => [...prev, { label: inputValue, value: inputValue }]);
                }}
                placeholder="Select or type city..."
                styles={{
                  control: (base) => ({
                    ...base,
                    borderRadius: "0.5rem",
                    borderColor: "#e2e8f0",
                    boxShadow: "none",
                    minHeight: "38px",
                    "&:hover": { borderColor: "#94a3b8" },
                  }),
                }}
              />
            </div>

            {/* State */}
            <div className="flex flex-col gap-1">
              <label className={labelClass}>State</label>
              <input name="state" value={formData.state} onChange={handleChange} className={inputClass} />
            </div>

            {/* Zip Code */}
            <div className="flex flex-col gap-1">
              <label className={labelClass}>Zip Code</label>
              <input name="zipCode" value={formData.zipCode} onChange={handleChange} className={inputClass} />
            </div>

            {/* Country */}
            <div className="flex flex-col gap-1">
              <label className={labelClass}>Country</label>
              <input name="country" value={formData.country} onChange={handleChange} className={inputClass} />
            </div>


            {/* Opening Balance */}
            <div className="flex flex-col gap-1">
              <label className={labelClass}>Opening Balance</label>
              <div className="grid grid-cols-2 gap-3">
                {/* AMOUNT */}
                <input
                  type="number"
                  min="0"
                  name="openingBalance"
                  value={formData.openingBalance}
                  onChange={handleChange}
                  className={inputClass}
                  placeholder="Amount"
                />

                {/* DEBIT / CREDIT */}
                <select
                  name="openingBalanceType"
                  value={formData.openingBalanceType}
                  onChange={handleChange}
                  className={inputClass}
                >
                  <option value="debit">Debit</option>
                  <option value="credit">Credit</option>
                </select>
              </div>
              {errors.openingBalance && (
                <p className="text-red-500 text-xs mt-1">
                  {errors.openingBalance}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* FOOTER */}
        <div className="px-6 py-4 border-t bg-white flex justify-end gap-3">
          <button
            type="button"
            onClick={() => {
              resetForm();
              onClose();
            }}
            className="bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold px-6 py-2 rounded-md transition-colors text-sm border border-slate-200"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading}
            className="bg-orange-500 hover:bg-orange-600 text-white font-semibold px-6 py-2 rounded-md transition-colors text-sm flex items-center gap-2 disabled:opacity-60"
          >
            {loading ? (
              <Loader text={isEdit ? "Updating..." : "Saving..."} inline />
            ) : (
              isEdit ? "Update Client" : "Add Client"
            )}
          </button>
        </div>
      </form>
    </div>,
    document.body
  );
};

export default AddClient;
